// components/kyc/KycVerificationDialog.tsx
//
// KYC verification dialog/modal

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIdVerification } from "@/hooks/useIdVerification";
import { Shield, ShieldCheck, Clock, XCircle, ExternalLink, Info, CheckCircle2, ScanFace } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import KycInfoDialog from "./KycInfoDialog";
import { authLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";
import {
  formatPersonalNumber,
  validatePersonalNumber,
} from "@/utils/formValidation";
import { UserService } from "@/services/user/userService";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface KycVerificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const KycVerificationDialog = ({ isOpen, onClose }: KycVerificationDialogProps) => {
  const {
    bankIdStatus,
    bankIdFailureReason,
    hasPersonalIdentityNumber,
    isApproved,
    isPending,
    isDeclined,
    needsVerification,
    startVerification,
    checkStatus,
    isStarting,
    hasStartedVerification,
    startFastPolling,
    stopFastPolling,
  } = useIdVerification();
  const queryClient = useQueryClient();
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<"bankid" | "didit" | null>(null);
  const [ssnStepOpen, setSsnStepOpen] = useState(false);
  const [personalNumber, setPersonalNumber] = useState("");
  const [isSavingSsn, setIsSavingSsn] = useState(false);

  const labels = useMemo(() => authLabels(getLanguage()), []);
  const ssnValidation = useMemo(
    () => validatePersonalNumber(personalNumber),
    [personalNumber],
  );

  // Track when session has been started
  useEffect(() => {
    if (hasStartedVerification) {
      setSessionStarted(true);
    }
  }, [hasStartedVerification]);

  useEffect(() => {
    if (!isStarting) {
      setSelectedMethod(null);
    }
  }, [isStarting]);

  useEffect(() => {
    if (!isOpen) {
      setSsnStepOpen(false);
      setPersonalNumber("");
      setIsSavingSsn(false);
    }
  }, [isOpen]);

  // Start fast polling when dialog opens and verification has started
  useEffect(() => {
    if (isOpen && hasStartedVerification) {
      startFastPolling();
    } else if (!isOpen) {
      stopFastPolling();
    }

    return () => {
      if (!isOpen) {
        stopFastPolling();
      }
    };
  }, [isOpen, hasStartedVerification, startFastPolling, stopFastPolling]);

  // Auto-close dialog when status becomes approved
  useEffect(() => {
    if (isApproved && isOpen) {
      setShowSuccessAnimation(true);
      // Show success animation for 3 seconds, then close
      const timer = setTimeout(() => {
        setShowSuccessAnimation(false);
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isApproved, isOpen, onClose]);

  const getStatusIcon = () => {
    if (isApproved) return ShieldCheck;
    if (isPending) return Clock;
    if (isDeclined) return XCircle;
    return Shield;
  };

  const getStatusColor = () => {
    if (isApproved) return "text-green-500";
    if (isPending) return "text-yellow-500";
    if (isDeclined) return "text-red-500";
    return "text-gray-500";
  };

  const getStatusText = () => {
    if (isApproved) return labels.kyc?.status?.approved;
    if (isPending) return labels.kyc?.status?.pendingStatus;
    if (isDeclined) return labels.kyc?.status?.declinedStatus;
    return labels.kyc?.status?.needsVerification;
  };

  const StatusIcon = getStatusIcon();

  const handleStartVerification = (method: "bankid" | "didit") => {
    if (method === "didit" && !hasPersonalIdentityNumber) {
      setSelectedMethod("didit");
      setSsnStepOpen(true);
      return;
    }

    setSelectedMethod(method);
    startVerification(method, {
      forceRestart: method === "bankid" && bankIdStatus === "pending",
    });
  };

  const handleSaveSsnAndStartDidit = async () => {
    if (!ssnValidation.isValid) {
      toast.error(
        ssnValidation.errors[0] ||
          labels.forms?.validation?.personalNumberRequired,
      );
      return;
    }

    setIsSavingSsn(true);
    try {
      await UserService.updateUserInfo({ personalNumber });
      await queryClient.invalidateQueries({ queryKey: ["kyc", "status"] });
      await queryClient.invalidateQueries({ queryKey: ["userDetails"] });
      setSsnStepOpen(false);
      setPersonalNumber("");
      setSelectedMethod("didit");
      startVerification("didit");
    } catch (error: any) {
      toast.error(
        error?.message ||
          labels.kyc?.dialog?.ssnRequired?.saveError ||
          "Kunde inte spara personnummer",
      );
    } finally {
      setIsSavingSsn(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[90vw] sm:max-w-[500px] md:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StatusIcon className={`h-5 w-5 ${getStatusColor()}`} />
              {labels.kyc?.title}
            </DialogTitle>
            <DialogDescription>
              {getStatusText()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {showSuccessAnimation ? (
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <div className="relative">
                  <CheckCircle2 className="h-20 w-20 text-green-500 animate-in zoom-in duration-500" />
                  <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-30" />
                  <div className="absolute inset-0 bg-green-500/20 rounded-full animate-pulse" />
                </div>
                <p className="text-xl font-bold text-green-600 dark:text-green-400 animate-in fade-in duration-500">
                  {labels.kyc?.success?.title}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center animate-in fade-in duration-700">
                  {labels.kyc?.success?.description}
                </p>
              </div>
            ) : ssnStepOpen ? (
              <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  {labels.kyc?.dialog?.ssnRequired?.description ||
                    "Ange ditt personnummer innan du startar ID-verifiering med Didit."}
                </p>
                <div className="space-y-2">
                  <Label htmlFor="kyc-personal-number">
                    {labels.forms?.fields?.personalNumber}
                  </Label>
                  <Input
                    id="kyc-personal-number"
                    value={personalNumber}
                    placeholder={labels.forms?.fields?.personalNumberPlaceholder}
                    autoComplete="off"
                    inputMode="numeric"
                    onChange={(e) =>
                      setPersonalNumber(formatPersonalNumber(e.target.value))
                    }
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSsnStepOpen(false);
                      setSelectedMethod(null);
                    }}
                    disabled={isSavingSsn}
                  >
                    {labels.kyc?.dialog?.ssnRequired?.back || "Tillbaka"}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => void handleSaveSsnAndStartDidit()}
                    disabled={!ssnValidation.isValid || isSavingSsn || isStarting}
                  >
                    {isSavingSsn || isStarting
                      ? labels.kyc?.dialog?.starting || "Startar..."
                      : labels.kyc?.dialog?.ssnRequired?.continue ||
                        "Spara och fortsätt"}
                  </Button>
                </div>
              </div>
            ) : !isApproved ? (
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <p>{labels.kyc?.dialog?.description}</p>
                <p>{labels.kyc?.dialog?.chooseMethod}</p>
                <div className="grid gap-4 pt-2 md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => handleStartVerification("bankid")}
                    disabled={isStarting}
                    className={`rounded-xl border p-4 text-left transition-all duration-200 ${
                      selectedMethod === "bankid"
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 shadow-[0_12px_30px_rgba(212,175,55,0.14)]"
                        : "border-slate-200 bg-white hover:border-[#D4AF37]/60 hover:shadow-[0_12px_30px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950"
                    } ${isStarting ? "cursor-wait opacity-70" : "cursor-pointer"}`}
                  >
                    <div className="flex h-full flex-col gap-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-16 w-16 items-center justify-center">
                          <img
                            src="/assets/BankID_logo.png"
                            alt="BankID"
                            className="h-10 w-auto object-contain"
                          />
                        </div>
                        <div>
                          <p className="text-[1.35rem] font-semibold leading-none text-slate-900 dark:text-slate-100">
                            {labels.kyc?.dialog?.methods?.bankId?.title}
                          </p>
                        </div>
                      </div>
                      <p>{labels.kyc?.dialog?.methods?.bankId?.description}</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>{labels.kyc?.dialog?.methods?.bankId?.steps?.sameDevice}</li>
                        <li>{labels.kyc?.dialog?.methods?.bankId?.steps?.qr}</li>
                      </ul>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleStartVerification("didit")}
                    disabled={isStarting}
                    className={`rounded-xl border p-4 text-left transition-all duration-200 ${
                      selectedMethod === "didit"
                        ? "border-slate-500 bg-slate-100 shadow-[0_12px_30px_rgba(71,85,105,0.14)] dark:bg-slate-900"
                        : "border-slate-200 bg-white hover:border-slate-400 hover:shadow-[0_12px_30px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950"
                    } ${isStarting ? "cursor-wait opacity-70" : "cursor-pointer"}`}
                  >
                    <div className="flex h-full flex-col gap-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-16 w-16 items-center justify-center">
                          <ScanFace className="h-8 w-8 text-slate-600 dark:text-slate-300" />
                        </div>
                        <div>
                          <p className="text-[1.35rem] font-semibold leading-tight text-slate-900 dark:text-slate-100">
                            {labels.kyc?.dialog?.methods?.didit?.title}
                          </p>
                        </div>
                      </div>
                      <p>{labels.kyc?.dialog?.methods?.didit?.description}</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>{labels.kyc?.dialog?.methods?.didit?.steps?.document}</li>
                        <li>{labels.kyc?.dialog?.methods?.didit?.steps?.selfie}</li>
                      </ul>
                    </div>
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {labels.kyc?.dialog?.gdpr}
                </p>
              </div>
            ) : null}

            {isPending && !showSuccessAnimation && !ssnStepOpen && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400 animate-spin" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      {labels.kyc?.dialog?.verifying}
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      {bankIdStatus === "pending"
                        ? labels.kyc?.dialog?.bankIdPending
                        : labels.kyc?.dialog?.checkingAutomatically}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isDeclined && !showSuccessAnimation && !ssnStepOpen && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                <p className="text-sm text-red-800 dark:text-red-200">
                  {bankIdFailureReason === "ssn_mismatch"
                    ? labels.kyc?.dialog?.declinedReasons?.ssnMismatch
                    : bankIdFailureReason === "ssn_already_used"
                      ? labels.kyc?.dialog?.declinedReasons?.ssnAlreadyUsed
                      : bankIdFailureReason === "missing_registered_ssn"
                        ? labels.kyc?.dialog?.declinedReasons?.missingRegisteredSsn
                        : labels.kyc?.dialog?.declinedMessage}
                </p>
              </div>
            )}
          </div>

          {!showSuccessAnimation && !ssnStepOpen && (
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setIsInfoDialogOpen(true)}
                className="w-full sm:w-auto"
              >
                <Info className="h-4 w-4 mr-2" />
                {labels.kyc?.dialog?.readMore}
              </Button>
              {needsVerification && !isPending && (
                <Button
                  onClick={() =>
                    handleStartVerification(
                      bankIdStatus === "pending" ? "bankid" : "didit",
                    )
                  }
                  disabled={isStarting}
                  className="w-full sm:w-auto"
                >
                  {isStarting
                    ? labels.kyc?.dialog?.starting
                    : labels.kyc?.dialog?.verifyIdentity}
                </Button>
              )}
              {isPending && (
                <Button
                  variant="secondary"
                  onClick={() => checkStatus()}
                  className="w-full sm:w-auto"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {labels.kyc?.dialog?.checkStatus}
                </Button>
              )}
              {sessionStarted && isDeclined && (
                <Button
                  onClick={() => handleStartVerification("didit")}
                  disabled={isStarting}
                  className="w-full sm:w-auto"
                >
                  {labels.kyc?.dialog?.startNewSession}
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <KycInfoDialog
        isOpen={isInfoDialogOpen}
        onClose={() => setIsInfoDialogOpen(false)}
      />
    </>
  );
};

export default KycVerificationDialog;
